/**
 * Страница списка образовательных коллекций
 * apps/web/src/pages/CollectionsPage.tsx
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../stores/collections';
import { isVK, isCoursesSite, detectPlatform } from '../platform/detect';
/** «1 модуль», «24 модуля», «27 модулей» — иначе карточка выглядит машинной. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/** С какого числа курсов список перестаёт читаться целиком и нужен фильтр. */
const TOO_MANY_TO_SCAN = 6;

/** С какого числа курсов глазами уже не пробежать — показываем строку поиска. */
const SEARCH_FROM = 8;

/**
 * Сколько карточек рисуем за раз. Раньше рисовались ВСЕ разом: при дюжине курсов
 * незаметно, при сотнях страница встанет. Остальное — по кнопке «Показать ещё».
 */
const PAGE_SIZE = 12;

/**
 * Отбор по строке поиска. Ищем по названию, описанию и направлению.
 *
 * Пока каталог маленький, ищем прямо по загруженному списку — это мгновенно и
 * не требует запросов. Когда курсов станут сотни, здесь меняется ОДНА функция
 * на запрос к серверу, а разметка и поведение остаются прежними.
 */
function matches(c: any, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const поле = [c.name, c.description, c.category, c.categoryName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  // Каждое слово запроса должно найтись — так «телеграм деньги» находит курс,
  // где эти слова стоят порознь.
  return q.split(/\s+/).every((слово) => поле.includes(слово));
}

/** Новым курс считается месяц. */
const NEW_FOR_DAYS = 30;

/** Сколько курсов должны иметь запуски, чтобы «Популярное» о чём-то говорило. */
const POPULAR_NEEDS_COURSES = 3;

// ==================== Компоненты ====================

function CategoryTabs() {
  const { categories, selectedCategory, selectCategory } = useCollectionsStore();

  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      <button
        onClick={() => selectCategory(null)}
        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
          !selectedCategory
            ? 'bg-blue-500 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Все
      </button>
      {categories.map((cat) => (
        <button
          key={cat.id}
          onClick={() => selectCategory(cat.id)}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
            selectedCategory === cat.id
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {cat.name} ({cat.count})
        </button>
      ))}
    </div>
  );
}

function CollectionCard({
  collection,
  onClick,
}: {
  collection: any;
  onClick: () => void;
}) {
  const difficultyLabels = {
    beginner: '🌱 Начальный',
    intermediate: '🌿 Средний',
    advanced: '🌳 Продвинутый',
  };

  const accessBadge = () => {
    if (collection.access.type === 'free') {
      return <span className="text-green-600 text-sm font-medium">🆓 Бесплатно</span>;
    }
    if (collection.access.type === 'premium') {
      return (
        <span className="text-amber-600 text-sm font-medium">
          ⭐ {collection.access.price} Stars
        </span>
      );
    }
    return <span className="text-blue-600 text-sm font-medium">🎁 Freemium</span>;
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 
                 hover:shadow-md hover:border-blue-200 transition-all text-left"
    >
      <div className="flex items-start gap-3">
        {collection.cover ? (
          <img
            src={collection.cover}
            alt=""
            loading="lazy"
            className="h-16 w-24 flex-none rounded-xl object-cover"
          />
        ) : (
          <div className="text-4xl">{collection.icon}</div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{collection.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
            {collection.description}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {/* Чего не знаем — не показываем: пустое «модулей ~ мин» выглядит поломкой */}
          {collection.stats.totalModules ? (
            <span>{plural(collection.stats.totalModules, 'модуль', 'модуля', 'модулей')}</span>
          ) : null}
          {collection.stats.estimatedMinutes ? (
            <span>~{collection.stats.estimatedMinutes} мин</span>
          ) : null}
        </div>
        {accessBadge()}
      </div>

      {collection.userProgress && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">
              {collection.userProgress.completed ? '✅ Пройдено' : 'Прогресс'}
            </span>
            <span className="font-medium text-blue-600">
              {collection.userProgress.completedPercent}%
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                collection.userProgress.completed ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${collection.userProgress.completedPercent}%` }}
            />
          </div>
        </div>
      )}

      {!collection.userProgress && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-gray-400">
            {difficultyLabels[collection.difficulty as keyof typeof difficultyLabels]}
          </span>
          {/* Кнопка обещает действие: «курс» сам по себе никуда не зовёт */}
          <span className="text-sm font-medium text-blue-600">Начать →</span>
        </div>
      )}
    </button>
  );
}

function ContinueBlock({
  items,
  onOpen,
}: {
  items: any[];
  onOpen: (id: string) => void;
}) {
  return (
    <section className="mb-6">
      <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
        Продолжить обучение
      </h2>
      {/* В браузере прохождение привязано к этому браузеру, а не к человеку:
          обещать «сохранено везде» мы не вправе. В Telegram оно едет за номером. */}
      {detectPlatform() === 'web' && (
        <p className="mb-2 text-xs text-gray-400">
          Прогресс хранится в этом браузере
        </p>
      )}
      <div className="space-y-3">
        {items.map((c) => (
          <button
            key={c.id}
            onClick={() => onOpen(c.id)}
            className="w-full bg-white rounded-2xl p-4 shadow-sm border border-blue-200
                       hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-3">
              <div className="text-3xl">{c.icon}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{c.name}</h3>
                <p className="text-sm text-gray-500">
                  {c.userProgress.completedPercent > 0
                    ? `Пройдено ${c.userProgress.completedPercent}%`
                    : 'Вы только начали'}
                </p>
              </div>
              <span className="text-blue-600 font-medium whitespace-nowrap">Продолжить →</span>
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.max(c.userProgress.completedPercent, 2)}%` }}
              />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function Shelf({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: any[];
  onOpen: (id: string) => void;
}) {
  return (
    <section className="mb-6">
      <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</h2>
      <div className="space-y-4">
        {items.map((c) => (
          <CollectionCard key={c.id} collection={c} onClick={() => onOpen(c.id)} />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">📚</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Коллекции не найдены
      </h3>
      <p className="text-gray-500">
        Попробуйте выбрать другую категорию
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 animate-pulse"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-xl" />
            <div className="flex-1">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-full" />
            </div>
          </div>
          <div className="mt-3 h-4 bg-gray-100 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

// ==================== Main Page ====================

export default function CollectionsPage() {
  const navigate = useNavigate();
  // На витрине курсов игровой части нет — незачем и звать в неё
  const coursesOnly = isCoursesSite();
  const {
    collections,
    categories,
    selectedCategory,
    isLoading,
    error,
    loadCategories,
    loadCollections,
  } = useCollectionsStore();

  // Считаем по всему каталогу, а не по отфильтрованному списку: иначе после выбора
  // направления курсов станет мало и фильтр исчезнет вместе с возможностью вернуться.
  const totalCourses = categories.reduce((sum, c) => sum + c.count, 0);
  const showTabs = categories.length > 1 && totalCourses > TOO_MANY_TO_SCAN;

  // Начатое и незаконченное — только когда показан весь каталог
  const continueItems = selectedCategory
    ? []
    : collections.filter((c: any) => c.userProgress?.started && !c.userProgress?.completed);

  // Полки имеют смысл только на большом каталоге: при коротком списке они его дублируют
  const wholeCatalog = !selectedCategory && collections.length > TOO_MANY_TO_SCAN;

  const withStarts = collections.filter((c: any) => (c.starts || 0) > 0);
  const popular =
    wholeCatalog && withStarts.length >= POPULAR_NEEDS_COURSES
      ? [...withStarts].sort((a: any, b: any) => (b.starts || 0) - (a.starts || 0)).slice(0, 4)
      : [];

  const monthAgo = Date.now() - NEW_FOR_DAYS * 24 * 60 * 60 * 1000;
  const fresh = collections.filter(
    (c: any) => c.createdAt && new Date(c.createdAt).getTime() > monthAgo,
  );
  // Если новое — половина каталога, подсказка «новинки» ничего не подсказывает
  const novelties = wholeCatalog && fresh.length && fresh.length <= collections.length / 2 ? fresh : [];

  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE_SIZE);

  const searching = query.trim().length > 0;
  const found = useMemo(
    () => (searching ? collections.filter((c: any) => matches(c, query)) : collections),
    [collections, query, searching],
  );
  const visible = found.slice(0, shown);

  // Новый запрос или другое направление — снова с первой порции, иначе человек
  // видит обрезанный список и не понимает, почему.
  useEffect(() => {
    setShown(PAGE_SIZE);
  }, [query, selectedCategory]);

  useEffect(() => {
    loadCategories();
    loadCollections();
  }, []);

  // Скрываем курсы в VK (правила модерации)
  if (isVK()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">🎮</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Курсы недоступны в VK</h2>
          <p className="text-gray-500 mb-6">Этот раздел недоступен в данном приложении</p>
          <button
            onClick={() => window.location.href = '/profile'}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium"
          >
            ← Вернуться в меню
          </button>
        </div>
      </div>
    );
  }

  const handleCollectionClick = (id: string) => {
    navigate(`/collections/${id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="px-4 py-4">
          {!coursesOnly && (
            <button
              onClick={() => window.location.href = '/'}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-all mb-3"
            >
              ← Меню
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {coursesOnly ? '🎓 Чему хотите научиться?' : '📚 Коллекции'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {coursesOnly
              ? 'Разбор на входе подскажет, с чего начать именно вам. После каждой темы — короткая проверка, за пройденный курс — сертификат.'
              : 'Изучай темы шаг за шагом'}
          </p>
        </div>
        {showTabs && (
          <div className="px-4 pb-3">
            <CategoryTabs />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Строка поиска — только когда список уже не пробежать глазами */}
        {!isLoading && collections.length >= SEARCH_FROM && (
          <div className="mb-4">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Найти курс: тема, слово из описания…"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white
                         text-gray-900 placeholder-gray-400 focus:outline-none
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {isLoading ? (
          <LoadingState />
        ) : collections.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Во время поиска полки прячем: человек ищет конкретное,
                а «Новое» и «Чаще всего начинают» ему сейчас мешают. */}
            {!searching && continueItems.length > 0 && (
              <ContinueBlock items={continueItems} onOpen={handleCollectionClick} />
            )}
            {!searching && novelties.length > 0 && (
              <Shelf title="✨ Новое" items={novelties} onOpen={handleCollectionClick} />
            )}
            {!searching && popular.length > 0 && (
              <Shelf title="🔥 Чаще всего начинают" items={popular} onOpen={handleCollectionClick} />
            )}
            {!searching &&
              (continueItems.length > 0 || novelties.length > 0 || popular.length > 0) && (
                <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Все курсы
                </h2>
              )}

            {searching && (
              <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                {found.length > 0
                  ? `Нашлось: ${plural(found.length, 'курс', 'курса', 'курсов')}`
                  : 'Ничего не нашлось'}
              </h2>
            )}

            {found.length === 0 ? (
              <div className="text-center py-10">
                <div className="text-5xl mb-3">🔎</div>
                <p className="text-gray-500">
                  По запросу ничего нет. Попробуйте другое слово или очистите поиск.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {visible.map((collection: any) => (
                    <CollectionCard
                      key={collection.id}
                      collection={collection}
                      onClick={() => handleCollectionClick(collection.id)}
                    />
                  ))}
                </div>

                {/* Рисуем порциями: при большом каталоге страница иначе встаёт */}
                {found.length > visible.length && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShown((n) => n + PAGE_SIZE)}
                      className="px-5 py-2.5 bg-white border border-gray-200 rounded-xl
                                 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Показать ещё ({found.length - visible.length})
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Ссылка на страницу о данных. Счётчик и вебвизор пишут поведение
            посетителей, и сайт обязан об этом сказать — 152-ФЗ и правила самой
            Метрики. Подвала у витрины не было вовсе, поэтому ставим скромной
            строкой под списком курсов (01.09.2026). */}
        {/* 04.09.2026: подвал стал полным. Модерация платёжного сервиса и покупатель
            должны с главной найти цены, условия, возврат и поддержку — не только политику. */}
        <div className="mt-10 pb-6 text-center text-xs text-gray-400">
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <a href="/pricing" className="hover:text-gray-600 transition-colors">Цены</a>
            <a href="/terms" className="hover:text-gray-600 transition-colors">Условия оказания услуг</a>
            <a href="/refund" className="hover:text-gray-600 transition-colors">Возврат</a>
            <a href="/privacy" className="hover:text-gray-600 transition-colors">Как мы обращаемся с данными</a>
            <a href="/support" className="hover:text-gray-600 transition-colors">Поддержка</a>
          </nav>
        </div>
      </div>
    </div>
  );
}
